import {inject, Injectable, Provider} from '@angular/core';
import {environment} from "../../../environments/environment";
import {provideTranslateLoader, TranslateLoader, TranslateService, TranslationObject} from "@ngx-translate/core";
import {StorageService} from "../storage/storage.service";
import {StorageItem} from "../storage/storage.item";
import {StorageEnum} from "../storage/storage.enum";
import {LoggingService} from "../logger/logging.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {I18nRepo} from "../../repositories/api/i18n.repo";
import {KeyValueDto} from "../../repositories/dto/key-value.dto";
import {UserService} from "../user.service";
import {BehaviorSubject, distinctUntilChanged, Observable} from "rxjs";
import {filter, map} from "rxjs/operators";
import {DateParser} from "./date-parser";
import {Locale} from "./locale";
import {isDefined, nonBlank, safeSplit} from "../../libs/utils/epic.utils";
import {Iso8601DateParser} from "./iso8601-date-parser";
import {RepoProvider} from "../../repositories/repo.provider";
import {StateService} from "../state/state.service";
import {InfoDto} from "../../repositories/dto/info.dto";
import {provideTranslateHttpLoader} from "@ngx-translate/http-loader";
import {AuthService} from "../auth/auth.service";
import {DateStruct} from "./date-struct";

export function provideI18nHttpLoader(): Provider {
  if (environment.tolgee.enable) {
    return provideTranslateLoader(() => i18nHttpTolgeeLoaderFactory(inject(HttpClient)));
  }
  // Relativ statt der Vorgabe '/assets/i18n/': Der Viewer liegt unter /<Fassung>/, nicht an der Wurzel der Domain.
  return provideTranslateHttpLoader({prefix: 'assets/i18n/', suffix: '.json'});
}

export function i18nHttpTolgeeLoaderFactory(http: HttpClient): TranslateLoader {
  return {
    getTranslation(lang: string): Observable<any> {
      const tolgee = environment.tolgee;
      const url: string = tolgee.url + '/v2/projects/' + tolgee.projectId + '/export?languages=' + lang + '&format=JSON&zip=false&structureDelimiter&filterTag=dashboard';
      const headers: HttpHeaders = new HttpHeaders().set('Accept', 'application/json').set('X-API-Key', tolgee.token);
      return http.get(url, {headers: headers});
    }
  };
}

@Injectable({providedIn: 'root'})
export class I18nService {

  protected readonly isDevSystem: boolean = environment.dev;

  private readonly log: LoggingService = inject(LoggingService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly storage: StorageService = inject(StorageService);
  private readonly userService: UserService = inject(UserService);
  private readonly state: StateService = inject(StateService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly i18nRepo: I18nRepo = inject(RepoProvider).getRepo(I18nRepo);

  private readonly langs: string[] = environment.i18n.langs;
  private readonly defaultLang: string = environment.i18n.defaultLang;
  private readonly defaultCountry: string = environment.i18n.defaultCountry;
  private readonly storageKey: string = environment.i18n.storageKey;
  private readonly storageUse: boolean = environment.i18n.storageUse;
  private readonly chosenStorageKey: string = 'i18n:chosen_language';
  private readonly currentLanguageCode: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly currentCountryCode: BehaviorSubject<string> = new BehaviorSubject<string>(this.defaultCountry);
  private readonly browserUse: boolean = environment.i18n.browserUse;
  private readonly storageType: StorageEnum = StorageEnum.LOCAL;
  private readonly countries: BehaviorSubject<KeyValueDto[]> = new BehaviorSubject<KeyValueDto[]>([]);

  constructor() {
    this.translateService.addLangs(this.langs);
    this.log.trace("I18N: set default language '{}'", [this.defaultLang]);
    this.translateService.setFallbackLang(this.defaultLang);
    this.state.getInfo().subscribe((i) => {
      // Nur in die aktive, also geladene Sprache: Ein Buendel aus diesem einen Schluessel gaelte als geladen, use() holte
      // die Datei der Sprache nie, und die Oberflaeche bliebe in der Rueckfallsprache.
      if (!this.isDevSystem) {
        this.mergeDynamicTranslations(this.translateService.getCurrentLang(), i);
      }
      this.translateService.onLangChange.subscribe((event) => this.mergeDynamicTranslations(event.lang, i));
    });
    this.getCurrentLanguageCode().subscribe((lang) => {
      this.log.debug('I18N: changed current language to "{}"', [lang]);
      this.translateService.use(lang);
      this.setLanguageCodeInStorage(lang);
    });
    this.userService.getUser().subscribe((user) => {
      this.log.trace('I18N: changed user to "{}"', [user.id]);
      this.currentLanguageCode.next(this.getChosenLanguageCode() ?? user.languageCode);
      this.currentCountryCode.next(user.countryCode);
    });
    this.initLang();
    this.authService.getAuthenticatedSuccess().subscribe(() => {
      this.i18nRepo.getCountries().subscribe((res) => this.countries.next(res.getDatas()));
    });
  }

  public hasTranslationKey(key: string): boolean {
    return nonBlank(key) && nonBlank((this.translateService as any).getTextToInterpolate(key));
  }

  public translate(key: string, params?: object): string {
    return this.translateService.instant(key, params);
  }

  public translateAsync(key: string, params?: object): Promise<string> {
    return new Promise<string>((resolve) => {
      this.translateService.get(key, params).subscribe((msg: string) => resolve(msg));
    });
  }

  public getCurrentLanguageCode(): Observable<string> {
    return this.currentLanguageCode.asObservable().pipe(
      distinctUntilChanged(),
      filter((l) => nonBlank(l))
    );
  }

  public getLanguageCodes(): string[] {
    return this.langs;
  }

  /**
   * Sprachwahl des Viewers: Sie geht der Sprache der Organisation vor und gilt bis zum Schliessen des Tabs, auch ueber
   * den Sprung in eine andere Fassung. dash bietet keine an - dort legt das Profil die Sprache fest.
   */
  public chooseLanguage(lang: string): void {
    if (this.langs.includes(lang)) {
      this.storage.setItem(new StorageItem<string>(this.chosenStorageKey, lang).inStorage(StorageEnum.SESSION).withoutExpire());
      this.currentLanguageCode.next(lang);
    }
  }

  public getCountries(): Observable<KeyValueDto[]> {
    return this.countries.asObservable();
  }

  public getTimezones(): Observable<KeyValueDto[]> {
    return this.i18nRepo.getTimezones().pipe(map((w) => w.getDatas()));
  }

  public getSectors(filterCountryCode?: string): Observable<KeyValueDto[]> {
    return this.i18nRepo.getSectors(filterCountryCode).pipe(map((w) => w.getDatas()));
  }

  public getDateParser(): DateParser {
    return Locale.of(this.currentLanguageCode.value, this.currentLanguageCode.value.toUpperCase()).getDateParser();
  }

  public getIso8601DateParser(): DateParser {
    return new Iso8601DateParser();
  }

  public getCommonLocale(): (any | undefined)[] {
    return Locale.of(this.currentLanguageCode.value, this.currentLanguageCode.value.toUpperCase()).getCommonLocale();
  }

  public toDate(value: Date | string | undefined): Date | undefined {
    const struct: DateStruct = this.getDateParser().parseLocalDate(value);
    if (isDefined(struct.day) && isDefined(struct.month) && isDefined(struct.year)) {
      const date = new Date();
      date.setUTCFullYear(struct.year, struct.month - 1, struct.day);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
    return undefined;
  }

  public getNextOccurringDate(date1: Date, date2: Date, currentDate?: Date): Date {
    const curDate: Date = isDefined(currentDate) ? currentDate : new Date();
    const currentYear: number = curDate.getFullYear();
    const date1ThisYear = new Date(currentYear, date1.getMonth(), date1.getDate());
    const date2ThisYear = new Date(currentYear, date2.getMonth(), date2.getDate());
    if (date1ThisYear < curDate) {
      date1ThisYear.setFullYear(currentYear + 1);
    }
    if (date2ThisYear < curDate) {
      date2ThisYear.setFullYear(currentYear + 1);
    }
    return date1ThisYear <= date2ThisYear ? date1ThisYear : date2ThisYear;
  }

  public addDaysToDate(date: Date, days: number): Date {
    const result = new Date(date);
    const originalDate = new Date(date);
    result.setDate(result.getDate() + days);

    const startYear: number = originalDate.getFullYear();
    const endYear: number = result.getFullYear();
    let leapDaysPassed: number = 0;

    for (let year: number = startYear; year <= endYear; year++) {
      if (this.isLeapYear(year)) {
        const feb29 = new Date(year, 1, 29);
        if (feb29 > originalDate && feb29 <= result) {
          leapDaysPassed++;
        }
      }
    }
    if (leapDaysPassed > 0) {
      result.setDate(result.getDate() + leapDaysPassed);
    }
    return result;
  }

  private isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }

  private initLang(): void {
    const chosenLang: string | undefined = this.getChosenLanguageCode();
    const storageLang: string | undefined = this.getLanguageCodeFromStorage();
    const browserLang: string | undefined = this.getLanguageCodeFromBrowser();
    if (isDefined(chosenLang)) {
      this.log.trace('I18N: use current language "{}" chosen in this tab', [chosenLang]);
      this.currentLanguageCode.next(chosenLang);
    } else if (this.storageUse && storageLang) {
      this.log.trace('I18N: use current language "{}" from storage', [storageLang]);
      this.currentLanguageCode.next(storageLang);
    } else if (this.browserUse && browserLang) {
      this.log.trace('I18N: use current language "{}" from browser', [storageLang]);
      this.currentLanguageCode.next(browserLang);
    } else {
      this.log.trace('I18N: use current language "{}" from default value', [storageLang]);
      this.currentLanguageCode.next(this.defaultLang);
    }
  }

  private setLanguageCodeInStorage(lang: string): void {
    if (this.storageUse) {
      this.storage.setItem(new StorageItem<string>(this.storageKey, lang).inStorage(this.storageType).withoutExpire());
    }
  }

  private getLanguageCodeFromStorage(): string | undefined {
    if (this.storageUse) {
      const storageLang: string | null = this.storage.getItem<string>(this.storageKey, this.storageType);
      if (isDefined(storageLang) && this.langs.indexOf(storageLang) !== -1) {
        return storageLang;
      }
    }
    return undefined;
  }

  private getChosenLanguageCode(): string | undefined {
    const chosenLang: string | null = this.storage.getItem<string>(this.chosenStorageKey, StorageEnum.SESSION);
    return isDefined(chosenLang) && this.langs.includes(chosenLang) ? chosenLang : undefined;
  }

  private getLanguageCodeFromBrowser(): string | undefined {
    if (this.browserUse) {
      const browserLang: string | undefined = this.translateService.getBrowserLang();
      if (isDefined(browserLang) && this.langs.indexOf(browserLang) !== -1) {
        return browserLang;
      }
      // eg. de-DE
      const browserCultureLang: string | undefined = this.translateService.getBrowserCultureLang();
      if (isDefined(browserCultureLang)) {
        const browserCultureLangFound: string | undefined = safeSplit(browserCultureLang, '-')
          .filter((bcLang) => this.langs.indexOf(bcLang) !== -1)
          .find((bcLang) => !!bcLang);
        if (browserCultureLangFound) {
          return browserCultureLangFound;
        }
      }
    }
    return undefined;
  }

  private mergeDynamicTranslations(lang: string | null, info: InfoDto): void {
    if (isDefined(lang) && nonBlank(lang)) {
      this.translateService.setTranslation(lang, this.buildDynamicTranslations(info), true);
    }
  }

  private buildDynamicTranslations(info: InfoDto): TranslationObject {
    return {'receipt.mediaType.accounting': info.name};
  }
}
